import React, { useState } from "react";
import { sbFetch } from "../utils/supabaseClient";
import { sendNotification } from "../utils/notifications";
import "../styles/CreateTab.css";

const NewsCreateTab = () => {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [publishedAt, setPublishedAt] = useState(new Date().toISOString().split('T')[0]);
  const [articleContent, setArticleContent] = useState("");
  const [whatItMeans, setWhatItMeans] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState("");

  const extractMetadataFromUrl = async (articleUrl) => {
    try {
      setLoading(true);

      // Use server-side metadata extraction via Cloudflare Worker
      const endpoints = [
        "https://content-parser-admin-api.james-blizzard-jr.workers.dev/api/fetch-news-metadata"
      ];

      let lastError = null;

      for (const endpoint of endpoints) {
        try {
          console.log("🔍 Attempting to fetch metadata from:", endpoint);
          const response = await fetch(endpoint, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ url: articleUrl }),
          });

          console.log("📡 Metadata fetch response status:", response.status);

          if (response.ok) {
            const data = await response.json();
            console.log("✓ Metadata received:", data);
            let extracted = false;
            let titleText = data.title || "";

            if (titleText && !title.trim()) {
              // Append publish date if found
              if (data.publishDate) {
                const dateOnly = data.publishDate.split('T')[0];
                titleText = `${titleText} (${dateOnly})`;
              }
              setTitle(titleText);
              extracted = true;
            }

            if (data.imageUrl && !imageUrl.trim()) {
              setImageUrl(data.imageUrl);
              extracted = true;
            }

            if (data.articleContent && !articleContent.trim()) {
              setArticleContent(data.articleContent);
              extracted = true;
              console.log("✓ Article content extracted, length:", data.articleContent.length);
            }

            if (extracted) {
              const extractedItems = [];
              if (titleText && !title.trim()) extractedItems.push("title");
              if (data.imageUrl && !imageUrl.trim()) extractedItems.push("image");
              if (data.articleContent && !articleContent.trim()) extractedItems.push("content");
              setMessage(`✅ Extracted: ${extractedItems.join(", ")}`);
              setTimeout(() => setMessage(""), 3000);
              return;
            } else {
              setMessage("ℹ️ No new metadata found for this article");
              setTimeout(() => setMessage(""), 2000);
              return;
            }
          } else {
            console.warn(`Metadata endpoint returned ${response.status}:`, await response.text());
            lastError = new Error(`HTTP ${response.status}`);
          }
        } catch (err) {
          lastError = err;
          console.error(`Endpoint ${endpoint} failed:`, err.message);
          continue;
        }
      }

      if (lastError) {
        console.error("All metadata extraction endpoints failed:", lastError);
      }

      // Silently fail - metadata extraction is optional
      console.log("Metadata extraction skipped, user can enter manually");
      return null;
    } catch (err) {
      console.error("Error extracting metadata:", err);
      setMessage("⚠️ Could not extract metadata automatically");
      setTimeout(() => setMessage(""), 3000);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleUrlBlur = async () => {
    if (!url.trim()) return;

    let urlToValidate = url.trim();

    // Auto-add https:// if no protocol provided
    if (!urlToValidate.match(/^https?:\/\//i)) {
      urlToValidate = `https://${urlToValidate}`;
      setUrl(urlToValidate);
    }

    try {
      new URL(urlToValidate);
      await extractMetadataFromUrl(urlToValidate);
    } catch {
      // URL is still invalid after adding protocol
    }
  };

  const generateInterpretation = async () => {
    if (!title.trim()) {
      setMessage("Please enter a title first");
      return;
    }

    setGenerating(true);
    try {
      const endpoints = [
        "https://content-parser-admin-api.james-blizzard-jr.workers.dev/api/generate-interpretation"
      ];

      for (const endpoint of endpoints) {
        try {
          const response = await fetch(endpoint, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              title: title.trim(),
              url: url.trim(),
              articleContent: articleContent.trim(),
            }),
          });

          if (!response.ok) {
            continue;
          }

          const data = await response.json();
          const interpretation = data.interpretation || "";

          if (interpretation) {
            setWhatItMeans(interpretation);
            setMessage("✅ Interpretation generated! Feel free to edit it.");
            setTimeout(() => setMessage(""), 3000);
            return;
          }
        } catch (err) {
          console.log(`Endpoint ${endpoint} failed:`, err);
          continue;
        }
      }

      setMessage("❌ AI interpretation unavailable. Check your Cloudflare Worker is deployed, or set REACT_APP_API_ENDPOINT in .env");
    } catch (err) {
      console.error("Error generating interpretation:", err);
      setMessage("❌ Failed to generate interpretation");
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!url.trim() || !title.trim()) {
      setMessage("URL and Title are required");
      return;
    }

    // Validate URL
    try {
      new URL(url);
    } catch {
      setMessage("Please enter a valid URL");
      return;
    }

    setSaving(true);
    try {
      const newNews = {
        url: url.trim(),
        title: title.trim(),
        image_url: imageUrl.trim(),
        published_at: publishedAt,
        what_does_it_mean_to_me: whatItMeans.trim(),
        archived: false,
      };

      await sbFetch("POST", "news", newNews);

      // Send notification with AI-generated message
      try {
        await sendNotification(
          `NEWS; ${title.trim()}`,
          title.trim(),
          "news",
          null,
          { type: "news article", title: title.trim(), url: url.trim(), description: articleContent.trim() }
        );
      } catch (notifyErr) {
        console.warn("Failed to send notification:", notifyErr);
      }

      setMessage("✅ News item saved successfully!");
      setUrl("");
      setTitle("");
      setImageUrl("");
      setArticleContent("");
      setWhatItMeans("");
      setPublishedAt(new Date().toISOString().split('T')[0]);

      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Error saving news:", err);

      // Check for duplicate URL error
      if (err.message && err.message.includes("23505")) {
        setMessage("⚠️ This URL is already in the news system");
      } else {
        setMessage("❌ Failed to save news item");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="create-container">
      <h2>Create News Item</h2>

      <form onSubmit={handleSubmit} className="create-form">
        <div className="form-group">
          <label>Article URL *</label>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onBlur={handleUrlBlur}
            placeholder="https://example.com/article"
            disabled={saving || loading}
          />
          {loading && <small style={{ color: "#5B9EF0", marginTop: "4px", display: "block" }}>Extracting image and metadata...</small>}
        </div>

        {imageUrl && (
          <div className="form-group">
            <label>Preview Image</label>
            <img
              src={imageUrl}
              alt="Article preview"
              style={{
                width: "100%",
                maxHeight: "200px",
                objectFit: "cover",
                borderRadius: "8px",
                border: "1px solid rgba(59, 130, 246, 0.15)"
              }}
            />
          </div>
        )}

        <div className="form-group">
          <label>Title *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="News headline"
            disabled={saving}
          />
        </div>

        <div className="form-group">
          <label>Published Date</label>
          <input
            type="date"
            value={publishedAt}
            onChange={(e) => setPublishedAt(e.target.value)}
            disabled={saving}
          />
        </div>

        <div className="form-group">
          <label>Article Content (Optional - paste for better AI analysis)</label>
          <textarea
            value={articleContent}
            onChange={(e) => setArticleContent(e.target.value)}
            placeholder="Paste the article text here for more accurate AI interpretation. If left empty, Claude will analyze based on title alone."
            disabled={saving}
            style={{ minHeight: "80px", fontFamily: "monospace", fontSize: "12px" }}
          />
          <small style={{ color: "#666", marginTop: "4px", display: "block" }}>
            Tip: The more context you provide, the better Claude's interpretation will be
          </small>
        </div>

        <div className="form-group">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <label>What It Means To You</label>
            <button
              type="button"
              onClick={generateInterpretation}
              disabled={generating || saving || !title.trim()}
              style={{
                padding: "6px 12px",
                background: "#2B7CE9",
                color: "white",
                border: "none",
                borderRadius: "6px",
                fontSize: "12px",
                fontWeight: "600",
                cursor: generating || saving ? "not-allowed" : "pointer",
                opacity: generating || saving ? 0.6 : 1,
              }}
            >
              {generating ? "⏳ Generating..." : "✨ AI Generate"}
            </button>
          </div>
          <textarea
            value={whatItMeans}
            onChange={(e) => setWhatItMeans(e.target.value)}
            placeholder="What does this news mean for leaders navigating AI adoption?"
            disabled={saving}
            style={{ minHeight: "100px" }}
          />
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={saving || loading}>
            {saving ? "Saving..." : "📰 Save News Item"}
          </button>
        </div>

        {message && (
          <div className={`message ${message.includes("✅") ? "success" : message.includes("⚠️") ? "warning" : "error"}`}>
            {message}
          </div>
        )}
      </form>
    </div>
  );
};

export default NewsCreateTab;
